import { useTranslation } from 'next-i18next'

import GemstoneProgress from '~/components/GemstoneProgress'
import { GridPosition } from '~/types/drill'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import CheckmarkCircleIcon from '~/components/Icons/svgs/CheckmarkCircleIcon'
import { Link } from '~/components/Link'

import Splat from './Splat'

export default function DrillTopicElement({
  buttonClassNames,
  color,
  completed,
  gemstoneOutlineColor,
  gridPosition,
  i18nKey,
  isActivity,
  score = 0,
  splatPosition,
  textClassNames,
  title,
  url,
}: {
  buttonClassNames: string
  color: string
  completed: boolean
  gemstoneOutlineColor?: 'white' | 'black'
  gridPosition: GridPosition
  i18nKey: 'go' | 'playAlt'
  isActivity?: boolean
  score?: number
  splatPosition: 'left' | 'right' | undefined
  textClassNames: string
  title: string
  url: string
}): React.ReactElement {
  const { t } = useTranslation('generics')

  function truncateTitle(elemTitle: string): string {
    const MAX_LENGTH = 45
    return elemTitle.length > MAX_LENGTH ? `${elemTitle.slice(0, MAX_LENGTH)}...` : elemTitle
  }

  const shadowClass = color === 'white' && buttonClassNames.includes('bg-white') ? 'drop-shadow-lg' : ''

  const sizeCx = 'w-45 h-[185px]'

  return (
    <div
      className={`relative transform hover:scale-[1.125] filter hover:drop-shadow-lg hover:z-50 transition ${sizeCx}`}
      style={{ ...gridPosition }}
    >
      <Background className="absolute" fill={color} />
      <Link to={url}>
        <Splat color={color} position={splatPosition} />
        <div className="relative h-full px-6 pt-10 pb-8">
          {completed && !isActivity && (
            <div className="absolute left-1/2 top-5 transform -translate-x-1/2">
              <SVGIcon icon={CheckmarkCircleIcon} pathClassName="text-green-checkmark" size={30} />
            </div>
          )}

          {isActivity && (
            <GemstoneProgress
              innerClassName="absolute left-1/2 top-6 transform -translate-x-1/2 flex"
              outlineColor={gemstoneOutlineColor}
              score={score}
            />
          )}

          <div className="flex flex-col justify-center items-center w-full h-full">
            <div className="mb-auto" />
            <div className={`text-center ${textClassNames}`}>{truncateTitle(title)}</div>
            <button className={`mt-auto px-5 py-1.5 rounded font-semibold ${buttonClassNames} ${shadowClass}`}>
              {t(i18nKey)}
            </button>
          </div>
        </div>
      </Link>
    </div>
  )
}

function Background({ className, fill }: { className: string; fill: string }): React.ReactElement {
  return (
    <svg className={className} fill="none" viewBox="0 0 252 266" xmlns="http://www.w3.org/2000/svg">
      <g>
        <path
          d="M116 8.7735C122.188 5.20085 129.812 5.20085 136 8.7735L225.119 60.2265C231.307 63.7992 235.119 70.4017 235.119 77.547V180.453C235.119 187.598 231.307 194.201 225.119 197.774L136 249.226C129.812 252.799 122.188 252.799 116 249.226L26.8808 197.774C20.6928 194.201 16.8808 187.598 16.8808 180.453V77.547C16.8808 70.4017 20.6928 63.7992 26.8808 60.2265L116 8.7735Z"
          fill={fill}
        />
      </g>
    </svg>
  )
}
